import "./App.css";
import { NextUIProvider } from "@nextui-org/react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import RoutesList from "./routes.json";
import { useEffect, useState, useMemo } from "react";
import getComponent from "./getComponent.js";
import ErrorBoundary from "./components/ErrorPage/ErrorBoundary.jsx";
import ServerIssue from "./components/ErrorPage/ServerIssue.jsx";
import LoadingPage from "./components/Loader/LoadingPage.jsx";

const renderRoutes = (routesList) => {
  return routesList.map((route, index) => {
    const { path, element, props, nestedRoutes } = route;
    const Component = getComponent(element);
    return (
      <Route key={index} path={path} element={<Component {...props} />}>
        {nestedRoutes && renderRoutes(nestedRoutes)}
      </Route>
    );
  });
};

// Simple global error handler to prevent console warnings
const setupGlobalErrorHandling = () => {
  // Prevent console warnings about duplicate keys
  const originalConsoleWarn = console.warn;
  console.warn = (...args) => {
    const message = args.join(' ');
    
    // Filter out specific warnings that are not critical
    if (message.includes('Encountered two children with the same key')) {
      console.log('🔧 Suppressed duplicate key warning:', message);
      return;
    }
    
    if (message.includes('Warning: Each child in a list should have a unique "key" prop')) {
      console.log('🔧 Suppressed React key warning:', message);
      return;
    }
    
    // Allow other warnings to pass through
    originalConsoleWarn.apply(console, args);
  };

  // Prevent unhandled promise rejections from crashing the app
  window.addEventListener('unhandledrejection', (event) => {
    console.log('🔧 Caught unhandled promise rejection:', event.reason);
    event.preventDefault(); // Prevent the default browser behavior
  });

  // Prevent unhandled errors from crashing the app
  window.addEventListener('error', (event) => {
    console.log('🔧 Caught unhandled error:', event.error);
    event.preventDefault(); // Prevent the default browser behavior
  });
};

// Initialize global error handling
setupGlobalErrorHandling();

function App() {
  if (process.env.NODE_ENV === "production") {
    console.log = console.warn = () => {};
  }

  const [serverIssue, setServerIssue] = useState(false);
  const [loader, setLoader] = useState(true);

  // Memoize the document title to prevent unnecessary updates
  const documentTitle = useMemo(() => {
    const isESG = process.env.REACT_APP_TYPE !== "SOC2";
    return isESG 
      ? "Audyt ESG | Sustainability Reporting" 
      : "Audyt SOC2 | Compliance Reporting";
  }, []);

  const fetchWithTimeout = (url, options, timeout = 5000) => {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), timeout)
      ),
    ]);
  };

  useEffect(() => {
    // Set document title only once
    document.title = documentTitle;

    const checkBackendConnection = async () => {
      try {
        const response = await fetchWithTimeout(
          `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}`
        );
       
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
      } catch (error) {
        setServerIssue(true);
      } finally {
        setLoader(false);
      }
    };

    checkBackendConnection();
  }, [documentTitle]);

  if (serverIssue) {
    return <ServerIssue />;
  }

  if (loader) {
    return (
      <div className="flex h-[100vh] items-center justify-center">
        <LoadingPage isLoading={true} />
      </div>
    );
  }
  
  return (
    // <ErrorBoundary>
      <NextUIProvider>
        <div>
          <BrowserRouter>
            <Routes>{renderRoutes(RoutesList)}</Routes>
          </BrowserRouter>
        </div>
      </NextUIProvider>
    // </ErrorBoundary>
  );
}

export default App;
